import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { PromoAdjustmentSearch } from '../types/promotion.types';
import {
  toPromoAdjustmentFilterSearch,
  toPromoAdjustmentFilterValues,
  toPromoZmSearch,
} from '../utils/promotionFilters';

const route = getRouteApi('/_auth/promotion/adjustment/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toPromoAdjustmentFilterValues(toPromoAdjustmentFilterSearch(values));

export function useAdjustmentFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(() => toPromoAdjustmentFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    shouldWaitForApply: true,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toPromoAdjustmentFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const itemSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): PromoAdjustmentSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  const drillParams = useMemo(() => toPromoZmSearch(params), [params]);

  return { ...filters, ...itemSearch, params, drillParams };
}
